import React from 'react';

import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import {
    moveBetweenLists,
    reAllocateChannels,
    removeChannel,
    setChannels,
} from '@fiji/common/src/features/widgetManagement/detailsWidgetSlice';
import { setGaugeChannels } from '@fiji/common/src/features/widgetManagement/gaugeWidgetSlice';
import { DropResult } from 'react-beautiful-dnd';
import { useGetChannelThresholdsQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import {
    moveBetweenChartLists,
    reAllocateChartChannels,
    removeChartChannel,
    setChartChannels,
} from '@fiji/common/src/features/widgetManagement/chartWidgetSlice';
import {
    reAllocateTrendsChannels,
    removeTrendsChannel,
    setTrendsChannels,
} from '@fiji/common/src/features/widgetManagement/trendsWidgetSlice';
import { reAllocateLoads, removeLoad, setLoads } from '@fiji/common/src/features/widgetManagement/loadsWidgetSlice';
import {
    moveBetweenAssetLists,
    reAllocateAsset,
    removeSelectedAsset,
} from '@fiji/common/src/features/widgetManagement/energyFlowWidgetSlice';
import {
    reAllocateAssetForMetrics,
    removeSelectedAssetForMetrics,
} from '@fiji/common/src/features/widgetManagement/metricsWidgetSlice';
import {
    reAllocateConsumptionAssets,
    removeAssetsForConsumptionBreakdown,
} from '@fiji/common/src/features/widgetManagement/consumptionBreakdownWidgetSlice';
import { removeLoadTrendsChannel } from '@fiji/common/src/features/widgetManagement/loadTrendsWidgetSlice';
import {
    reAllocateWeatherAssets,
    removeAssetsForWeather,
} from '@fiji/common/src/features/widgetManagement/weatherWidgetSlice';
import { reallocateDeviceList, removeDevice } from '@fiji/common/src/features/widgetManagement/deviceListWidgetSlice';
import {
    reallocateTimelineFilters,
    removeTimelineFilters,
} from '@fiji/common/src/features/widgetManagement/timelineWidgetSlice';
import { reallocateGroupList, removeGroup } from '@fiji/common/src/features/widgetManagement/groupListWidgetSlice';
import {
    removeGroupLinkedToMap,
    reallocateMapSelectedNodes,
} from '@fiji/common/src/features/widgetManagement/mapWidgetSlice';
import {
    reallocateUpcomingFilters,
    removeUpcomingNode,
} from '@fiji/common/src/features/widgetManagement/upcomingWidgetSlice';
import {
    reallocateEnvironmentalStressMonitoringFilters,
    removeEnvironmentalStressMonitoringNode,
} from '@fiji/common/src/features/widgetManagement/environmentalStressMonitoringWidgetSlice';
import {
    reAllocateCommandChannels,
    removeDeviceCommand,
    setCommandChannels,
    setWidgetKey,
} from '@fiji/common/src/features/widgetManagement/commandBarWidgetSlice';
import { removeSchedule } from '@fiji/common/src/features/widgetManagement/scheduleWidgetSlice';
import {
    reallocateComponentFilters,
    removeComponentNode,
} from '@fiji/common/src/features/widgetManagement/componentWidgetSlice';
import { reallocateScoreFilters, removeScoreNode } from '@fiji/common/src/features/widgetManagement/scoreWidgetSlice';
import {
    reAllocateAggregatedTrendsAssets,
    removeAssetsForAggregatedTrends,
} from '@fiji/common/src/features/widgetManagement/aggregatedTrendsWidgetSlice';
import {
    reAllocateLoadGroups,
    removeLoadGroups,
    setLoadGroups,
} from '@fiji/common/src/features/widgetManagement/loadGroupWidgetSlice';
import {
    reAllocateLoadGroupTrends,
    removeLoadGroupTrends,
    setLoadGroupTrends,
} from '@fiji/common/src/features/widgetManagement/loadGroupTrendsSlice';

const DRAG_END_ACTION_MAP: Record<string, (payload: any) => any> = {
    charts: reAllocateChartChannels,
    trends: reAllocateTrendsChannels,
    loads: reAllocateLoads,
    load_groups: reAllocateLoadGroups,
    load_group_trends: reAllocateLoadGroupTrends,
    energy_flow: reAllocateAsset,
    metrics: reAllocateAssetForMetrics,
    consumption_breakdown: reAllocateConsumptionAssets,
    load_trends: removeLoadTrendsChannel,
    weather: reAllocateWeatherAssets,
    aggregated_trends: reAllocateAggregatedTrendsAssets,
    device_list: reallocateDeviceList,
    timeline: reallocateTimelineFilters,
    components: reallocateComponentFilters,
    upcoming: reallocateUpcomingFilters,
    environmental_stress_monitoring: reallocateEnvironmentalStressMonitoringFilters,
    score: reallocateScoreFilters,
    group_list: reallocateGroupList,
    map: reallocateMapSelectedNodes,
    command_bar: reAllocateCommandChannels,
};

const REMOVE_CHANNEL_ACTION_MAP: Record<string, (payload: any) => any> = {
    charts: removeChartChannel,
    trends: removeTrendsChannel,
    loads: removeLoad,
    load_groups: removeLoadGroups,
    load_group_trends: removeLoadGroupTrends,
    energy_flow: removeSelectedAsset,
    metrics: removeSelectedAssetForMetrics,
    consumption_breakdown: removeAssetsForConsumptionBreakdown,
    load_trends: removeLoadTrendsChannel,
    weather: removeAssetsForWeather,
    aggregated_trends: removeAssetsForAggregatedTrends,
    device_list: removeDevice,
    timeline: removeTimelineFilters,
    group_list: removeGroup,
    schedule: removeSchedule,
    map: removeGroupLinkedToMap,
    upcoming: removeUpcomingNode,
    environmental_stress_monitoring: removeEnvironmentalStressMonitoringNode,
    components: removeComponentNode,
    score: removeScoreNode,
    command_bar: removeDeviceCommand,
};

type ReturnProps = {
    onDragEnd: (args: DropResult) => void;
    handleRemoveChannel: (args: any) => void;
    handleChecked: (args: any, args1?: any, args2?: any) => boolean;
    isRadioSelected: (args: any) => boolean;
    handleGaugeChannelSelection: (args: any) => void;
    handleChannelSelection: (args0: any, args1?: any) => void;
    handleChannelTypeSelection: (args: any) => string;
    isCommandPaletteChecked: (arg: any, currentType: string, currentColor: string) => boolean;
};

export const useDefaultConfig = (widgetTypeId: string, data?: any): ReturnProps => {
    const dispatch = useAppDispatch();
    const channelPayload = useTypedSelector((state) => state[widgetTypeId]);
    const configurationPayload = useTypedSelector((deviceData) => deviceData.widgetConfiguration);
    const commandPaletteEl = useTypedSelector((state) => state.command_palette.commandPaletteEl);

    useGetChannelThresholdsQuery(
        {
            deviceId: configurationPayload?.device?.id,
            channelId: configurationPayload?.data?.channel?.channelId,
        },
        { skip: !configurationPayload?.device?.id || !configurationPayload?.data?.channel?.channelId }
    );

    React.useEffect(() => {
        if (data && widgetTypeId === 'gauge' && configurationPayload?.device?.id && !channelPayload?.data) {
            dispatch(setGaugeChannels({ ...data.data[0], deviceId: configurationPayload.device.id }));
        }
    }, [widgetTypeId, data, configurationPayload]);

    function channelSelectionHandlers(payload: any, palettePayload?: any): any {
        switch (widgetTypeId) {
            case 'charts':
                dispatch(setChartChannels(payload));
                break;
            case 'trends':
                dispatch(setTrendsChannels(payload));
                break;
            case 'loads':
                dispatch(setLoads(payload));
                break;
            case 'load_groups':
                dispatch(setLoadGroups(payload));
                break;
            case 'load_group_trends':
                dispatch(setLoadGroupTrends(payload));
                break;
            case 'command_bar':
                if (palettePayload) dispatch(setWidgetKey(palettePayload));
                if (payload) dispatch(setCommandChannels(payload));
                break;
            default:
                dispatch(setChannels(payload));
                break;
        }
    }

    function isCommandPaletteChecked(payload: any, currentType: string, currentColor: string): boolean {
        const selectedChannel = payload?.secondary?.find(
            (item: any) =>
                item?.channelId === commandPaletteEl?.channelId && item?.deviceId === commandPaletteEl?.deviceId
        );
        return (
            selectedChannel?.paletteConfig?.color === currentColor &&
            selectedChannel?.paletteConfig?.type === currentType
        );
    }

    function dragEndHandlers(payload: any): void {
        const action = DRAG_END_ACTION_MAP[widgetTypeId] ?? reAllocateChannels;
        dispatch(action(payload));
    }

    function dragBetweenListHandlers(payload: any): any {
        switch (widgetTypeId) {
            case 'charts':
                dispatch(moveBetweenChartLists(payload));
                break;
            case 'trends':
                dispatch(moveBetweenChartLists(payload));
                break;
            case 'energy_flow':
                dispatch(moveBetweenAssetLists(payload));
                break;
            default:
                dispatch(moveBetweenLists(payload));
                break;
        }
    }

    function removeChannelHandlers(payload: any): void {
        const action = REMOVE_CHANNEL_ACTION_MAP[widgetTypeId] ?? removeChannel;
        dispatch(action(payload));
    }

    const onDragEnd = (result: DropResult): void => {
        if (result.destination) {
            const { source, destination } = result;
            if (source.droppableId === destination.droppableId) {
                dragEndHandlers({
                    type: source?.droppableId,
                    sourceIndex: source?.index,
                    destinationIndex: destination?.index,
                });
            } else {
                // Move between lists
                dragBetweenListHandlers({
                    draggableId: source.droppableId,
                    droppableId: destination.droppableId,
                    sourceIndex: source.index,
                    destinationIndex: destination.index,
                });
            }
        }
    };

    const handleRemoveChannel = (payload: any): void => {
        removeChannelHandlers(payload);
    };

    const handleChecked = (channel: any, customTraverseKey?: string, targetedKey?: string): boolean => {
        if (widgetTypeId === 'details') {
            switch (true) {
                case Boolean(channelPayload?.primary?.length) && Boolean(channelPayload?.secondary?.length):
                    return channelPayload?.primary
                        ?.concat(channelPayload?.secondary)
                        ?.some((item: any) => channel?.channelId === item?.channelId);
                case Boolean(channelPayload?.primary?.length):
                    return channelPayload?.primary?.some((item: any) => channel?.channelId === item?.channelId);
                case Boolean(channelPayload?.secondary?.length):
                    return channelPayload?.secondary?.some((item: any) => channel?.channelId === item?.channelId);
                default:
                    return false;
            }
        }
        if (targetedKey) {
            return channelPayload?.[customTraverseKey ?? 'secondary']?.some(
                (item: any) => channel?.[targetedKey] === item?.[targetedKey]
            );
        }
        return channelPayload?.[customTraverseKey ?? 'secondary']?.some(
            (item: any) =>
                channel?.[widgetTypeId === 'loads' ? 'loadId' : 'channelId'] ===
                item?.[widgetTypeId === 'loads' ? 'loadId' : 'channelId']
        );
    };

    const handleChannelTypeSelection = (channel: any): string => {
        if (channelPayload?.secondary?.some((item: any) => channel?.channelId === item?.channelId)) {
            return 'secondary';
        }
        return 'primary';
    };

    const handleChannelSelection = (payload: any, palettePayload?: any): void => {
        channelSelectionHandlers(payload, palettePayload);
    };

    const handleGaugeChannelSelection = (payload: any): void => {
        dispatch(setGaugeChannels(payload));
    };

    const isRadioSelected = (channelId: any): boolean => channelPayload?.data?.channelId === channelId;

    return {
        onDragEnd,
        handleGaugeChannelSelection,
        isRadioSelected,
        handleRemoveChannel,
        handleChecked,
        handleChannelTypeSelection,
        handleChannelSelection,
        isCommandPaletteChecked,
    };
};
